// WHAT: Tab component for displaying shortfalls in planned vs worked time
// WHY:  Provides detailed view of time shortfalls to help identify areas needing attention
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: ShortfallEntry type
// RISKS:
//   - NOTE: Severity thresholds are hardcoded - may not match user expectations
//   - NOTE: Progress calculation may not handle edge cases
//   - NOTE: View mode toggle may not update display correctly
//   - NOTE: Color coding may not match design system

import type { ShortfallEntry } from "@/domains/productivity/timetracq/hooks/useDataFetcher";
import type { FC } from "react";

import { TrendingDown, Calendar, Clock, AlertTriangle, CheckCircle, Target } from "lucide-react";

import { Button, Card } from "@/components/ui";

/**
 * WHAT:
 *   Determines shortfall severity level.
 *
 * WHY:
 *   Provides visual indication of shortfall importance.
 *
 * @param {number} shortfall - Shortfall amount in hours
 *
 * @returns {"critical" | "high" | "medium" | "low"} Severity level
 *
 */
const getShortfallSeverity = (shortfall: number): "critical" | "high" | "low" | "medium" => {
  if (shortfall >= 20) {
    return "critical";
  }
  if (shortfall >= 10) {
    return "high";
  }
  if (shortfall >= 5) {
    return "medium";
  }
  return "low";
};

/**
 * WHAT:
 *   Gets color classes for severity level.
 *
 * WHY:
 *   Provides consistent visual styling for severity.
 *
 * @param {string} severity - Severity level string
 *
 * @returns {string} CSS class string for severity styling
 *
 */
const getSeverityColor = (severity: string): string => {
  switch (severity) {
    case "critical": {
      return "border-red-500/50 text-red-400";
    }
    case "high": {
      return "border-tracq-deep-orange/50 text-tracq-deep-orange";
    }
    case "medium": {
      return "border-yellow-500/50 text-yellow-400";
    }
    default: {
      return "border-slate-500/50 text-slate-400";
    }
  }
};

/**
 * WHAT:
 *   Calculates progress percentage (worked/planned).
 *
 * WHY:
 *   Enables progress bar visualization.
 *
 * @param {number} worked - Hours worked
 *
 * @param {number} planned - Hours planned
 *
 * @returns {number} Progress percentage (0-100)
 *
 */
const getProgressPercentage = (worked: number, planned: number): number => {
  if (planned === 0) {
    return 0;
  }
  return Math.min((worked / planned) * 100, 100);
};

/**
 * WHAT:
 *   Gets severity indicator className based on severity level.
 *
 * WHY:
 *   Provides consistent visual styling for severity indicators.
 *
 * @param {string} severity - Severity level
 *
 * @returns {string} CSS class string for severity indicator
 *
 */
const getSeverityIndicatorClass = (severity: string): string => {
  if (severity === "critical") {
    return "w-3 h-3 rounded-full bg-red-500";
  }
  if (severity === "high") {
    return "w-3 h-3 rounded-full bg-tracq-deep-orange";
  }
  if (severity === "medium") {
    return "w-3 h-3 rounded-full bg-yellow-500";
  }
  return "w-3 h-3 rounded-full bg-slate-500";
};

/**
 * WHAT:
 *   Gets severity badge className based on severity level.
 *
 * WHY:
 *   Provides consistent visual styling for severity badges.
 *
 * @param {string} severity - Severity level
 *
 * @returns {string} CSS class string for severity badge
 *
 */
const getSeverityBadgeClass = (severity: string): string => {
  if (severity === "critical") {
    return "px-2 py-0.5 rounded-md text-xs font-medium border border-red-500/30 text-red-400 bg-red-500/10";
  }
  if (severity === "high") {
    return "px-2 py-0.5 rounded-md text-xs font-medium border border-tracq-deep-orange/30 text-tracq-deep-orange bg-tracq-deep-orange/10";
  }
  if (severity === "medium") {
    return "px-2 py-0.5 rounded-md text-xs font-medium border border-yellow-500/30 text-yellow-400 bg-yellow-500/10";
  }
  return "px-2 py-0.5 rounded-md text-xs font-medium border border-slate-500/30 text-slate-400 bg-slate-500/10";
};

/**
 * WHAT:
 *   Gets progress bar className based on progress percentage.
 *
 * WHY:
 *   Provides consistent visual styling for progress bars.
 *
 * @param {number} progress - Progress percentage (0-100)
 *
 * @returns {string} CSS class string for progress bar
 *
 */
const getProgressBarClass = (progress: number): string => {
  if (progress >= 80) {
    return "h-2 rounded-full transition-all duration-500 bg-green-500";
  }
  if (progress >= 60) {
    return "h-2 rounded-full transition-all duration-500 bg-yellow-500";
  }
  if (progress >= 40) {
    return "h-2 rounded-full transition-all duration-500 bg-tracq-deep-orange";
  }
  return "h-2 rounded-full transition-all duration-500 bg-red-500";
};

/**
 * Props for ShortfallsTab component
 */
interface Properties {
  /** Set view mode handler */
  setShortfallViewMode: (mode: "full" | "today") => void;
  /** Shortfall entries */
  shortfalls: ShortfallEntry[];
  /** View mode (today or full month) */
  shortfallViewMode: "full" | "today";
}

/**
 * WHAT:
 *   Tab component that displays shortfalls in planned vs worked time with
 *   severity indicators and progress visualization.
 *
 * WHY:
 *   Enables users to identify areas where actual time spent falls short of
 *   planned time, helping prioritize work.
 *
 * PROPS:
 *   - shortfalls: ShortfallEntry[] — Shortfall data
 *   - shortfallViewMode: 'today' | 'full' — View mode
 *   - setShortfallViewMode: (mode) => void — Mode change handler
 *
 * BEHAVIOR:
 *   - Filters shortfalls based on view mode
 *   - Sorts by shortfall amount
 *   - Displays severity indicators
 *   - Shows progress bars
 *   - Calculates total shortfall
 *
 * RISKS:
 *   - NOTE: Severity thresholds are hardcoded - may not match user expectations
 *   - NOTE: Progress calculation may not handle edge cases
 *   - NOTE: View mode toggle may not update display correctly
 *   - NOTE: Color coding may not match design system
 *
 * @param {Properties} props - Component props
 *
 * @param {ShortfallEntry[]} props.shortfalls - Shortfall data entries
 *
 * @param {"full" | "today"} props.shortfallViewMode - Current view mode
 *
 * @param {(mode: "full" | "today") => void} props.setShortfallViewMode - Handler to change view mode
 *
 * @returns {JSX.Element} Shortfalls tab component
 *
 */
export const ShortfallsTab: FC<Properties> = ({
  setShortfallViewMode,
  shortfalls,
  shortfallViewMode,
}) => {
  // Reason: TypeScript incorrectly infers shortfalls.filter() as error type

  const filteredShortfalls: ShortfallEntry[] = shortfalls
    .filter((s: ShortfallEntry) =>
      shortfallViewMode === "today" ? s.shortfallUntilToday > 0 : s.shortfallFullMonth > 0,
    )
    .toSorted((a: ShortfallEntry, b: ShortfallEntry) =>
      shortfallViewMode === "today"
        ? b.shortfallUntilToday - a.shortfallUntilToday
        : b.shortfallFullMonth - a.shortfallFullMonth,
    );

  const totalShortfall: number = filteredShortfalls.reduce(
    (sum: number, s: ShortfallEntry) =>
      sum + (shortfallViewMode === "today" ? s.shortfallUntilToday : s.shortfallFullMonth),
    0,
  );

  if (filteredShortfalls.length === 0) {
    return (
      <div className="h-full flex flex-col">
        {/* Header */}
        <Card
          className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card mb-6"
          padding="lg"
          variant="elevated"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Shortfalls</h3>
                <p className="text-slate-400 text-sm">Rückstände in deinen Arbeitsbereichen</p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setShortfallViewMode("today");
                }}
                size="sm"
                variant={shortfallViewMode === "today" ? "primary" : "secondary"}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Bis Heute
              </Button>
              <Button
                onClick={() => {
                  setShortfallViewMode("full");
                }}
                size="sm"
                variant={shortfallViewMode === "full" ? "primary" : "secondary"}
              >
                <Target className="w-4 h-4 mr-2" />
                Monatsziel
              </Button>
            </div>
          </div>
        </Card>

        {/* Empty State */}
        <Card
          className="flex-1 flex flex-col items-center justify-center text-center bg-slate-700/30 border-slate-600/50"
          padding="lg"
          variant="elevated"
        >
          <div className="w-16 h-16 mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h4 className="text-xl font-semibold text-white mb-2">Keine Rückstände! 🎉</h4>
          <p className="text-slate-400 max-w-md">
            {shortfallViewMode === "today"
              ? "Du bist bis heute mit allen Arbeitsbereichen im Plan."
              : "Du bist auf dem besten Weg, alle Monatsziele zu erreichen."}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col space-y-6">
      {/* Header mit Statistiken - Modernisiertes Layout */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
        padding="lg"
        variant="elevated"
      >
        <div className="space-y-6">
          {/* Titel und Info */}
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-white">Shortfalls</h3>
              <p className="text-slate-400">Rückstände in deinen Arbeitsbereichen</p>
            </div>
          </div>

          {/* Statistiken Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-600/30 rounded-xl p-4 border border-slate-500/30">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <TrendingDown className="w-4 h-4 text-red-400" />
                </div>
                <div>
                  <div className="text-sm text-slate-400">Gesamt-Rückstand</div>
                  <div className="text-xl font-bold text-red-400">{totalShortfall.toFixed(1)}h</div>
                </div>
              </div>
            </div>

            <div className="bg-slate-600/30 rounded-xl p-4 border border-slate-500/30">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-500/20 flex items-center justify-center">
                  <Target className="w-4 h-4 text-slate-300" />
                </div>
                <div>
                  <div className="text-sm text-slate-400">Bereiche</div>
                  <div className="text-xl font-bold text-white">{filteredShortfalls.length}</div>
                </div>
              </div>
            </div>

            <div className="bg-slate-600/30 rounded-xl p-4 border border-slate-500/30">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-tracq-deep-orange/20 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-tracq-deep-orange" />
                </div>
                <div>
                  <div className="text-sm text-slate-400">Kritisch</div>
                  <div className="text-xl font-bold text-tracq-deep-orange">
                    {
                      filteredShortfalls.filter(
                        (s: ShortfallEntry) =>
                          getShortfallSeverity(
                            shortfallViewMode === "today"
                              ? s.shortfallUntilToday
                              : s.shortfallFullMonth,
                          ) === "critical",
                      ).length
                    }
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-600/30 rounded-xl p-4 border border-slate-500/30">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-yellow-400" />
                </div>
                <div>
                  <div className="text-sm text-slate-400">Durchschnitt</div>
                  <div className="text-xl font-bold text-yellow-400">
                    {filteredShortfalls.length > 0
                      ? (totalShortfall / filteredShortfalls.length).toFixed(1)
                      : "0"}
                    h
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mode Toggle - Integriert in Header */}
          <div className="flex gap-3 pt-4 border-t border-slate-600/50">
            <Button
              className="flex-1"
              onClick={() => {
                setShortfallViewMode("today");
              }}
              size="sm"
              variant={shortfallViewMode === "today" ? "primary" : "secondary"}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Bis Heute
            </Button>
            <Button
              className="flex-1"
              onClick={() => {
                setShortfallViewMode("full");
              }}
              size="sm"
              variant={shortfallViewMode === "full" ? "primary" : "secondary"}
            >
              <Target className="w-4 h-4 mr-2" />
              Monatsziel
            </Button>
          </div>
        </div>
      </Card>

      {/* Shortfalls Liste - Modernisiertes Layout */}
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-4">
          {filteredShortfalls.map((s: ShortfallEntry) => {
            const shortfall: number =
              shortfallViewMode === "today" ? s.shortfallUntilToday : s.shortfallFullMonth;
            const planned: number = shortfallViewMode === "today" ? s.plannedUntilToday : s.planned;
            const severity = getShortfallSeverity(shortfall);
            const progress: number = getProgressPercentage(s.worked, planned);

            return (
              <Card
                className={`group bg-slate-800/30 hover:bg-slate-800/40 transition-all duration-200 border-slate-600/40 hover:border-slate-500/60 hover:scale-[1.01] ${getSeverityColor(severity)}`}
                key={`${s.parent}-${s.child}-${shortfallViewMode}`}
                variant="default"
              >
                <div className="p-4">
                  {/* Header mit Severity-Indikator */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {/* Severity-Indikator als farbiger Punkt */}
                      <div className={getSeverityIndicatorClass(severity)} />

                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-lg font-semibold text-white">{s.parent}</h4>
                          <span className={getSeverityBadgeClass(severity)}>
                            {severity.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-slate-400 text-sm">{s.child}</p>
                      </div>
                    </div>

                    {/* Shortfall Zeit */}
                    <div className="text-right">
                      <div className="text-xl font-bold text-red-400">-{shortfall.toFixed(1)}h</div>
                      <div className="text-xs text-slate-500">Rückstand</div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-400">Fortschritt</span>
                      <span className="text-slate-400">{progress.toFixed(0)}%</span>
                    </div>
                    <div className="w-full bg-slate-700/50 rounded-full h-2">
                      <div
                        className={getProgressBarClass(progress)}
                        style={{ width: `${String(progress)}%` }}
                      />
                    </div>
                  </div>

                  {/* Detailstatistiken */}
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-xs text-slate-400 mb-1">Gearbeitet</div>
                      <div className="font-semibold text-white">{s.worked.toFixed(1)}h</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-slate-400 mb-1">
                        {shortfallViewMode === "today" ? "Soll (heute)" : "Soll (Monat)"}
                      </div>
                      <div className="font-semibold text-white">{planned.toFixed(1)}h</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-slate-400 mb-1">Status</div>
                      <div className="font-semibold text-white capitalize">{s.status}</div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};
